import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  login,
  AuthType,
  type LoginByPhonePwdRequest,
  type LoginByAccountRequest,
  type LoginResponse,
} from '@/api'
import { GlobalCode, StatusCode } from '@/enums/api'
import { showToast } from 'vant'
import router from '@/router'
import { getDevice, toastify } from '@/utils'
import { clearCustomerServiceUserInfo } from '@/utils/customerService'
import { useChatStore } from './chat'

/**
 * 认证 Store
 * 管理登录状态、用户信息和记住密码功能
 */
export const useAuthStore = defineStore(
  'auth',
  () => {
    // 登录状态
    const isAuthenticated = ref(false)
    const token = ref<string | null>(null)
    const userInfo = ref<any>(null)

    // 记住密码 - 手机号登录
    const mobileRemember = ref(false)
    const savedMobilePhone = ref('')
    const savedMobilePassword = ref('')

    // 记住密码 - 账号登录
    const accountRemember = ref(false)
    const savedAccountUsername = ref('')
    const savedAccountPassword = ref('')

    // 登录成功后操作
    async function afterLoginSuccess() {
      isAuthenticated.value = true
      const chatStore = useChatStore()
      await chatStore.connectWebSocket()
    }

    /**
     * 手机号密码登录
     */
    async function loginByPhone(values: LoginByPhonePwdRequest, remember: boolean): Promise<void> {
      try {
        const res = await login({
          area_code: '86',
          phone: values.phone,
          password: values.password,
          type: AuthType.PhonePwd,
          device_id: getDevice(),
        })

        if (res.code == GlobalCode.Success) {
          isAuthenticated.value = true
          const chatStore = useChatStore()
          await chatStore.connectWebSocket()
        }

        // 处理记住密码
        if (remember) {
          mobileRemember.value = true
          savedMobilePhone.value = values.phone
          savedMobilePassword.value = values.password
        } else {
          // 清除记住的密码
          mobileRemember.value = false
          savedMobilePhone.value = ''
          savedMobilePassword.value = ''
        }

        // 登录成功后：有 redirect 则跳回原页面，否则跳首页
        const redirect = router.currentRoute.value.query.redirect
        const target =
          typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')
            ? redirect
            : '/'
        router.replace(target)
      } catch (error: any) {
        // 处理登录错误
        let message = error.message || ''
        if (error.code == StatusCode.WrongPassword && error.data.attempts) {
          message = `密码错误，您还有 ${error.data.attempts.left_attempts as number} 次尝试机会`
        }
        toastify({
          type: 'danger',
          message,
        })
        throw error
      }
    }

    /**
     * 账号密码登录
     */
    async function loginByAccount(values: LoginByAccountRequest, remember: boolean): Promise<void> {
      try {
        const res = await login({
          type: AuthType.AccountPassword,
          account: values.account.trim(),
          password: values.password,
          device_id: getDevice(),
        })

        if (res.code == GlobalCode.Success) {
          isAuthenticated.value = true
          const chatStore = useChatStore()
          await chatStore.connectWebSocket()
        }

        // 处理记住密码
        if (remember) {
          accountRemember.value = true
          savedAccountUsername.value = values.account.trim()
          savedAccountPassword.value = values.password
        } else {
          // 清除记住的密码
          accountRemember.value = false
          savedAccountUsername.value = ''
          savedAccountPassword.value = ''
        }

        // 登录成功后：有 redirect 则跳回原页面，否则跳首页
        const redirect = router.currentRoute.value.query.redirect
        const target =
          typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')
            ? redirect
            : '/'
        router.replace(target)
      } catch (e: unknown) {
        const error = e as LoginResponse
        console.log('Login error:', error)
        // 处理登录错误
        let message = error.message || ''
        if (error.code == StatusCode.WrongPassword && error.data.attempts) {
          message = `密码错误，您还有 ${error.data.attempts.left_attempts as number} 次尝试机会`
        }
        toastify({
          type: 'danger',
          message,
        })
        throw e
      }
    }

    /**
     * 登出
     */
    function logout() {
      isAuthenticated.value = false
      token.value = null
      userInfo.value = null
      // 清除客服访客信息
      clearCustomerServiceUserInfo()
    }

    /**
     * 清除手机号记住的密码
     */
    function clearMobileRemember() {
      mobileRemember.value = false
      savedMobilePhone.value = ''
      savedMobilePassword.value = ''
    }

    /**
     * 清除账号记住的密码
     */
    function clearAccountRemember() {
      accountRemember.value = false
      savedAccountUsername.value = ''
      savedAccountPassword.value = ''
    }

    /**
     * 获取手机号记住的凭据
     */
    function getMobileCredentials() {
      if (mobileRemember.value && savedMobilePhone.value && savedMobilePassword.value) {
        return {
          phone: savedMobilePhone.value,
          password: savedMobilePassword.value,
          remember: true,
        }
      }
      return null
    }

    /**
     * 获取账号记住的凭据
     */
    function getAccountCredentials() {
      if (accountRemember.value && savedAccountUsername.value && savedAccountPassword.value) {
        return {
          account: savedAccountUsername.value,
          password: savedAccountPassword.value,
          remember: true,
        }
      }
      return null
    }

    return {
      // State
      isAuthenticated,
      token,
      userInfo,
      mobileRemember,
      savedMobilePhone,
      savedMobilePassword,
      accountRemember,
      savedAccountUsername,
      savedAccountPassword,

      // Actions
      loginByPhone,
      loginByAccount,
      logout,
      clearMobileRemember,
      clearAccountRemember,
      getMobileCredentials,
      getAccountCredentials,
      afterLoginSuccess,
    }
  },
  {
    persist: {
      // 持久化配置
      storage: localStorage,
      pick: [
        // 只持久化记住密码相关的字段
        'mobileRemember',
        'savedMobilePhone',
        'savedMobilePassword',
        'accountRemember',
        'savedAccountUsername',
        'savedAccountPassword',
        // 登录状态也持久化
        'isAuthenticated',
        'token',
        'userInfo',
      ],
    },
  },
)
